import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { tasksApi, type DownloadRead, type TaskStatus } from '@/api'
import { wsURL } from '@/api/ws'

// Cap matches the backend's tasks.HistoryLimit so we never grow unbounded
// when many short-lived tasks fire in a session.
const MAX_ITEMS = 200

// Reconnect backoff: 1s, 2s, 4s, 8s, 15s, capped at 30s.
const RECONNECT_BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 30000]

interface WSIn {
    type: 'snapshot' | 'ack' | 'error' | 'list_changed' | 'download'
    task?: TaskStatus
    download?: DownloadRead
    op?: string
    topics?: string[]
    code?: string
    message?: string
    task_id?: number
}

export const useTasksStore = defineStore('tasks', () => {
    // ── state ──────────────────────────────────────────────────────────────
    const items = ref<TaskStatus[]>([])
    const loading = ref(false)
    const error = ref<string | null>(null)
    const connected = ref(false)

    let socket: WebSocket | null = null
    let reconnectAttempts = 0
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let wantOpen = false
    // Changed downloads pushed by the API (downloads-live-updates).
    const downloadListeners = new Set<(dl: DownloadRead) => void>()

    // ── getters ────────────────────────────────────────────────────────────
    const hasRunning = computed(() =>
        items.value.some(t => t.status === 'queued' || t.status === 'running' || t.status === 'cancelling')
    )
    const runningCount = computed(() =>
        items.value.filter(t => t.status === 'queued' || t.status === 'running' || t.status === 'cancelling').length
    )

    // ── actions ────────────────────────────────────────────────────────────
    async function refresh() {
        loading.value = true
        try {
            items.value = await tasksApi.list()
            error.value = null
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to load tasks'
        } finally {
            loading.value = false
        }
    }

    /** Merge a single task snapshot into items[], newest-first ordered. */
    function mergeTask(task: TaskStatus) {
        const idx = items.value.findIndex(t => t.job_id === task.job_id)
        if (idx !== -1) {
            items.value[idx] = task
            return
        }
        items.value.unshift(task)
        if (items.value.length > MAX_ITEMS) {
            items.value.length = MAX_ITEMS
        }
    }

    function connect() {
        if (!wantOpen) return
        if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
            return
        }
        const url = wsURL('/api/v1/tasks/ws')
        const ws = new WebSocket(url)
        socket = ws

        ws.addEventListener('open', () => {
            connected.value = true
            reconnectAttempts = 0
            ws.send(JSON.stringify({ op: 'subscribe', topics: ['all', 'downloads'] }))
        })

        ws.addEventListener('message', (ev) => {
            let msg: WSIn
            try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') } catch { return }
            if (msg.type === 'snapshot' && msg.task) {
                mergeTask(msg.task)
            } else if (msg.type === 'download' && msg.download) {
                for (const fn of downloadListeners) fn(msg.download)
            } else if (msg.type === 'error') {
                error.value = msg.message ?? 'websocket error'
            }
        })

        ws.addEventListener('close', () => {
            connected.value = false
            socket = null
            scheduleReconnect()
        })

        ws.addEventListener('error', () => {
            // The 'close' listener does the work; just close to ensure cleanup.
            ws.close()
        })
    }

    function scheduleReconnect() {
        if (!wantOpen) return
        if (reconnectTimer !== null) return
        const delay = RECONNECT_BACKOFF_MS[Math.min(reconnectAttempts, RECONNECT_BACKOFF_MS.length - 1)]
        reconnectAttempts++
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null
            // Refresh state via HTTP first so any updates we missed during the
            // disconnect are reflected immediately on the next open.
            refresh().finally(connect)
        }, delay)
    }

    /**
     * Open the websocket subscription. Idempotent — safe to call repeatedly;
     * the second-and-subsequent calls are no-ops while a connection is alive.
     * Now invoked from App.vue on auth, so the connection lives for the whole
     * authenticated session rather than only while TasksView is mounted.
     */
    function start() {
        if (wantOpen) return
        wantOpen = true
        refresh().finally(connect)
    }

    /** Close the websocket subscription. Call on view unmount. */
    function stop() {
        wantOpen = false
        if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer)
            reconnectTimer = null
        }
        if (socket !== null) {
            try { socket.close() } catch { /* ignore */ }
            socket = null
        }
        connected.value = false
        reconnectAttempts = 0
    }

    /** Calls fn with every download change while subscribed; returns the unsubscribe. */
    function onDownload(fn: (dl: DownloadRead) => void): () => void {
        downloadListeners.add(fn)
        return () => downloadListeners.delete(fn)
    }

    async function cancel(jobId: number) {
        try {
            const updated = await tasksApi.abort(jobId)
            mergeTask(updated)
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to cancel task'
        }
    }

    async function clearCompleted() {
        try {
            await tasksApi.clearCompleted()
            await refresh()
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to clear tasks'
        }
    }

    return {
        items,
        loading,
        error,
        connected,
        hasRunning,
        runningCount,
        refresh,
        start,
        stop,
        // Aliases kept for any callers still using the old names; safe no-ops
        // if a caller imports them — both forward to start/stop.
        startPolling: start,
        stopPolling: stop,
        onDownload,
        cancel,
        clearCompleted,
    }
})
